import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  formatDateTime,
  formatDuration,
  messages,
  type SendAttempt,
} from '@nesc/shared';
import { api, errorMessage, type SingleResponse } from '@/lib/api-client';
import { qk } from '@/lib/query-client';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { StatusBadge } from '@/components/common/status-badge';
import { ConfirmDialog } from '@/components/common/feedback';

/** 送信詳細。入力内容・スクリーンショット・判定根拠・再送 */
export const SendAttemptDialog = ({
  attemptId,
  onOpenChange,
}: {
  attemptId: string | null;
  onOpenChange: (open: boolean) => void;
}) => {
  const queryClient = useQueryClient();
  const [confirmResult, setConfirmResult] = useState<'success' | 'failed' | null>(null);

  const { data } = useQuery({
    queryKey: qk.sendAttempt(attemptId ?? ''),
    queryFn: () =>
      api.get<SingleResponse<SendAttempt & { message: { subject: string | null; body: string } | null }>>(
        `/send-attempts/${attemptId}`,
      ),
    enabled: !!attemptId,
  });

  const markResult = useMutation({
    mutationFn: (result: 'success' | 'failed') =>
      api.post(`/send-attempts/${attemptId}/mark-result`, {
        result,
        note: result === 'success' ? '人が確認し、送信されたと判断しました' : '人が確認し、送信されていないと判断しました',
      }),
    onSuccess: () => {
      toast.success('結果を確定しました');
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['send-attempts'] });
      setConfirmResult(null);
      onOpenChange(false);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const attempt = data?.data;

  return (
    <>
      <Dialog open={!!attemptId} onOpenChange={onOpenChange}>
        <DialogContent className="lg:max-w-2xl">
          <DialogHeader>
            <DialogTitle>送信の詳細</DialogTitle>
            {attempt && (
              <DialogDescription>
                {attempt.companyName}・{formatDateTime(attempt.sentAt)}
              </DialogDescription>
            )}
          </DialogHeader>

          <DialogBody className="space-y-4">
            {!attempt ? (
              <p className="text-body text-foreground-muted">{messages.common.loading}</p>
            ) : (
              <>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge kind="send_result" value={attempt.result} size="lg" />
                  <StatusBadge kind="channel" value={attempt.channel} />
                  {attempt.isSafeMode && (
                    <span className="rounded-control border border-dashed border-border-strong px-2 text-note">
                      セーフモード（実送信なし）
                    </span>
                  )}
                </div>

                {attempt.resultDetail && (
                  <div>
                    <p className="text-note font-semibold text-foreground-muted">判定根拠</p>
                    <p className="whitespace-pre-wrap text-body">{attempt.resultDetail}</p>
                  </div>
                )}

                {attempt.errorLabel && (
                  <p className="rounded-control border-2 border-foreground bg-muted p-3 text-body font-semibold">
                    {attempt.errorLabel}
                  </p>
                )}

                <dl className="divide-y divide-border rounded-card border border-border">
                  <Row label="送信先">{attempt.contactValue ?? '—'}</Row>
                  {attempt.messageId && <Row label="Message-ID">{attempt.messageId}</Row>}
                  <Row label="所要時間">{formatDuration(attempt.durationMs)}</Row>
                  {attempt.campaignName && <Row label="キャンペーン">{attempt.campaignName}</Row>}
                </dl>

                {attempt.requestPayload && Object.keys(attempt.requestPayload).length > 0 && (
                  <details className="rounded-card border border-border p-3">
                    <summary className="cursor-pointer text-body font-semibold">入力内容</summary>
                    <dl className="mt-2 space-y-1 text-note">
                      {Object.entries(attempt.requestPayload).map(([key, value]) => (
                        <div key={key} className="flex gap-2">
                          <dt className="w-32 shrink-0 text-foreground-muted">{key}</dt>
                          <dd className="min-w-0 flex-1 break-all">{String(value)}</dd>
                        </div>
                      ))}
                    </dl>
                  </details>
                )}

                {(attempt.screenshotBeforeUrl || attempt.screenshotAfterUrl) && (
                  <div className="grid gap-3 sm:grid-cols-2">
                    {attempt.screenshotBeforeUrl && (
                      <figure className="space-y-1">
                        <figcaption className="text-note text-foreground-muted">送信前</figcaption>
                        <img
                          src={attempt.screenshotBeforeUrl}
                          alt="送信前のページ"
                          className="w-full rounded-control border border-border"
                          loading="lazy"
                        />
                      </figure>
                    )}
                    {attempt.screenshotAfterUrl && (
                      <figure className="space-y-1">
                        <figcaption className="text-note text-foreground-muted">送信後</figcaption>
                        <img
                          src={attempt.screenshotAfterUrl}
                          alt="送信後のページ"
                          className="w-full rounded-control border border-border"
                          loading="lazy"
                        />
                      </figure>
                    )}
                  </div>
                )}

                {attempt.responseSnapshot && (
                  <details className="rounded-card border border-border p-3">
                    <summary className="cursor-pointer text-body font-semibold">送信後のページ内容</summary>
                    <p className="mt-2 whitespace-pre-wrap text-note text-foreground-muted">
                      {attempt.responseSnapshot}
                    </p>
                  </details>
                )}

                {attempt.message && (
                  <details className="rounded-card border border-border p-3">
                    <summary className="cursor-pointer text-body font-semibold">送信した文面</summary>
                    <p className="mt-2 text-body font-semibold">{attempt.message.subject}</p>
                    <p className="mt-1 whitespace-pre-wrap text-body text-foreground-muted">
                      {attempt.message.body}
                    </p>
                  </details>
                )}
              </>
            )}
          </DialogBody>

          <DialogFooter>
            {attempt && ['unknown', 'needs_action'].includes(attempt.result) && (
              <>
                <Button variant="outline" size="lg" onClick={() => setConfirmResult('failed')}>
                  {messages.campaign.markNotSent}
                </Button>
                <Button size="lg" onClick={() => setConfirmResult('success')}>
                  {messages.campaign.markSent}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirmResult}
        onOpenChange={(open) => !open && setConfirmResult(null)}
        title="送信結果を確定する"
        summary={
          confirmResult === 'success'
            ? 'この送信を「送信成功」として記録します。'
            : 'この送信を「送信失敗」として記録します。'
        }
        detail={
          <p className="text-body text-foreground-muted">
            確定すると集計に反映されます。内容を確認してから実行してください。
          </p>
        }
        confirmText="確定する"
        onConfirm={() => confirmResult && markResult.mutate(confirmResult)}
        isPending={markResult.isPending}
      />
    </>
  );
};

const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="flex gap-3 px-3 py-2">
    <dt className="w-32 shrink-0 text-note text-foreground-muted">{label}</dt>
    <dd className="min-w-0 flex-1 break-all text-body">{children}</dd>
  </div>
);
